import { useEffect, useState } from "react";
import { Textarea } from "@headlessui/react";
import { GoPersonAdd } from "react-icons/go";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import { ButtonLoader4 } from "../../components/ButtonLoader";
import * as Yup from "yup";
import { getAccessToken } from "../../storage/storage";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const ContactFormSchema = Yup.object({
  email: Yup.string().email("Email must be a valid email").required("Enter your email").max(250, "Email must be at most 250 characters"),
    first_name: Yup.string()
    .required("Enter your first name")
    .max(50, "First name must be at most 50 characters")
    .test("not-empty", "First name cannot be only spaces", value => value && value.trim().length > 0)
    .matches(
      /^[A-Za-z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes"
    )
    .test("min-trimmed-length", "First name must be at least 2 characters", value => value && value.trim().length >= 2),
  surname: Yup.string().required("Enter your surname"),
  // mobile_number: Yup.string().required("Enter your mobile number"),
  message: Yup.string().max(128, "Message must be at most 128 characters"),
  property: Yup.object().required("Select your property"),
});

const initialValues = {
  first_name: "",
  surname: "",
  email: "",
  message: "",
  mobile_number: "",
  property: null,
};

const InviteCleaner = () => {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const Navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/properties/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setOptions(response.data);
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
      });
  }, []);

  const { values, errors, handleChange, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues: initialValues,
      validationSchema: ContactFormSchema,
      onSubmit: async (values, action) => {
        setLoading(true);

        const payload = {
          ...values,
          property: values.property.id,
        };

        await axios
          .post(`${BASE_URL}/api/invitations/`, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            // toast.success("Invitation Send to Cleaner");
            Swal.fire({
              icon: "success",
              title: "Invitation Sent to Cleaner",
              timer: 2000,
              showConfirmButton: false,
              position: "center",
            });
            sessionStorage.setItem("CleanerData", "true");
            Navigate("/send-invitation");
            setLoading(false);
            action.resetForm();
          })
          .catch((error) => {
            console.error("Error:", error);
            Swal.fire({
              icon: "error",
              title: error.response?.data?.error || "Something went wrong!",
              timer: 2000,
              showConfirmButton: false,
              position: "center",
            });
            // toast.error(error.response?.data?.error || "Something went wrong");
            setLoading(false);
          });
      },
    });

  return (
    <>
      <div className="flex flex-col items-center justify-center mt-8">
        <GoPersonAdd className="text-[50px] font-semibold text-[#586989] pb-2" />
        <h1 className="md:text-3xl text-lg font-bold text-[#586989] mb-4 text-center">
          Invite Cleaner
        </h1>
        <p className="text-gray-600 mb-6 text-center">
          Send an invitation to a cleaner to join your team
        </p>
      </div>
      <div className="max-w-[800px] mx-auto bg-[#FFF5CE] p-6 rounded-lg shadow-lg mb-10">
        <h2 className="text-xl font-bold mb-4">Cleaner Information</h2>

        {/* Property Selection */}
        <div className="flex flex-col gap-2 my-5">
          <label className="text-[#000000] text-base font-normal">
            Select Property
          </label>

          <select
            name="property"
            value={values.property ? values.property.id : ""}
            onChange={(e) => {
              const selectedProperty = options.find(
                (property) => property.id === parseInt(e.target.value)
              );
              setFieldValue("property", selectedProperty || null);
            }}
            className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
          >
            <option value="">Select a Property</option>
            {options.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>

          {errors.property && touched.property && (
            <p className="text-red-500 text-[12px]">{errors.property}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* First Name */}
          <div className="flex flex-col gap-2">
            <label className="text-[#000000] text-base font-normal">
              First Name*
            </label>
            <input
              type="text"
              name="first_name"
              // placeholder="First Name*"
              value={values.first_name}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.first_name && touched.first_name && (
              <p className="text-red-500 text-[12px]">{errors.first_name}</p>
            )}
          </div>

          {/* Surname */}
          <div className="flex flex-col gap-2">
            <label className="text-[#000000] text-base font-normal">
              Surname*
            </label>
            <input
              type="text"
              name="surname"
              // placeholder="Surname*"
              value={values.surname}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.surname && touched.surname && (
              <p className="text-red-500 text-[12px]">{errors.surname}</p>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col gap-2">
            <label className="text-[#000000] text-base font-normal">
              Email Address*
            </label>
            <input
              type="text"
              name="email"
              // placeholder="Email Address*"
              value={values.email}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.email && touched.email && (
              <p className="text-red-500 text-[12px]">{errors.email}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div className="flex flex-col gap-2">
            <label className="text-[#000000] text-base font-normal">
              Mobile Number
            </label>
            <input
              type="text"
              name="mobile_number"
              // placeholder="Mobile Number"
              value={values.mobile_number}
              onChange={handleChange}
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg"
            />
            {errors.mobile_number && touched.mobile_number && (
              <p className="text-red-500 text-[12px]">{errors.mobile_number}</p>
            )}
          </div>
        </div>

        {/* Message */}
        <div className="flex flex-col gap-2 mt-5">
          <label className="text-[#000000] text-base font-normal">
            Invitation Message
          </label>
          <Textarea
            name="message"
            value={values.message}
            // placeholder="Invitation Message"
            onChange={handleChange}
            className="w-full p-2 border outline-none h-[120px] resize-none px-4 pt-4 rounded-lg"
          />
          {errors.message && touched.message && (
            <p className="text-red-500 text-[12px]">{errors.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-8 pb-8">
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-[#586989] text-white w-[130px] h-[50px] rounded-md"
          >
            {loading ? <ButtonLoader4 /> : "Send Invitation"}
          </button>
        </div>
      </div>
    </>
  );
};

export default InviteCleaner;
